import { styled } from "simplydo/react/styled";
import { Image } from "simplydo/react/ui/image";
import { Label } from "simplydo/react/ui/label";

import { buttonBaseClass } from "../button/buttonBase";
import { Icon } from "../icon";
import type { UISize } from "../utils/UIContext";
import { CONTROL_HEIGHT, SIZE_FONT } from "../utils/colors";
import type { DropdownVariant } from "./Dropdown.types";

export type DropdownStyleProps = {
  $variant?: DropdownVariant;
  $fluid?: boolean;
  $combo?: boolean;
  $disabled?: boolean;
  $size?: UISize;
};

export const StyledDropdownText = styled.div`
  display: inline-block;
  transition: none;

  &.is-default {
    color: rgba(191, 191, 191, 0.87);
  }
`;

export const StyledDropdownSearch = styled.input`
  position: absolute;
  z-index: 2;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: inherit;
  border: none;
  background: transparent;
  box-shadow: none;
  outline: none;
  cursor: text;
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  box-sizing: border-box;
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
  /* Parent uses user-select: none; inputs must opt back in to type. */
  user-select: text;
  -webkit-user-select: text;
`;

export const StyledDropdownMessage = styled.div`
  padding: 0.8em 1.1em;
`;

export const StyledDropdownItemDescription = styled.span`
  margin-left: auto;
  padding-left: 1em;
  color: rgba(0, 0, 0, 0.4);
`;

export const StyledDropdown = styled.div<DropdownStyleProps>`
  position: relative;
  display: ${({ $fluid }) => ($fluid ? "flex" : "inline-flex")};
  align-items: center;
  flex-wrap: ${({ $combo }) => ($combo ? "wrap" : "nowrap")};
  outline: 0;
  text-align: left;
  user-select: none;
  box-sizing: border-box;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  vertical-align: ${({ $variant }) =>
    $variant === "outline" ? "middle" : "baseline"};
  font-size: ${({ $size, $variant }) =>
    $variant === "outline" ? SIZE_FONT[$size || "medium"] : "inherit"};
  font-weight: ${({ $variant }) =>
    $variant === "outline" ? "normal" : "inherit"};
  line-height: ${({ $variant }) =>
    $variant === "outline" ? "1em" : "inherit"};
  color: ${({ $disabled, $variant }) =>
    $variant !== "outline"
      ? "inherit"
      : $disabled
        ? "rgba(0, 0, 0, 0.4)"
        : "rgba(0, 0, 0, 0.87)"};
  min-width: ${({ $variant }) => ($variant === "outline" ? "12em" : "0")};
  max-width: ${({ $variant }) => ($variant === "outline" ? "100%" : "none")};
  width: ${({ $fluid, $variant }) => {
    if ($fluid) return "100%";
    if ($variant === "outline") return "min-content";
    return "auto";
  }};
  min-height: ${({ $size, $variant }) =>
    $variant === "outline" ? CONTROL_HEIGHT[$size || "medium"] : "auto"};
  height: ${({ $combo, $size, $variant }) => {
    if ($variant !== "outline") return "auto";
    if ($combo) return "max-content";
    return CONTROL_HEIGHT[$size || "medium"];
  }};
  padding: ${({ $variant, $combo }) => {
    if ($variant !== "outline") return "0";
    if ($combo) return "0.2em 2.5em 0.2em 0.3em";
    return "0 2.5em 0 1em";
  }};
  margin: ${({ $variant }) => ($variant === "outline" ? "0" : "0 0.3rem")};
  background: ${({ $disabled, $variant }) => {
    if ($variant !== "outline") return "transparent";
    return $disabled ? "#fafafa" : "#ffffff";
  }};
  border: ${({ $variant }) =>
    $variant === "outline" ? "1px solid rgba(34, 36, 38, 0.15)" : "none"};
  border-radius: 0.3rem;
  overflow: ${({ $variant, $combo }) =>
    $variant === "outline" && !$combo ? "hidden" : "visible"};
  white-space: ${({ $variant, $combo }) =>
    $variant === "outline" && $combo ? "normal" : "nowrap"};
  word-wrap: ${({ $variant, $combo }) =>
    $variant === "outline" && $combo ? "break-word" : "normal"};
  transition:
    box-shadow 0.1s ease,
    border-color 0.1s ease,
    background 0.1s ease;

  &.is-trigger {
    padding: 0;
    min-height: 0;
    height: auto;
    margin: 0;
    background: transparent;
    border: none;
    vertical-align: middle;
  }

  /* Nested submenu: Dropdown wrapping a DropdownItem trigger inside a menu. */
  &.is-trigger.is-item-trigger {
    position: relative;

    .submenu-caret {
      margin-right: 0;
      position: absolute;
      left: 0;
      opacity: 0.4;
    }

    .submenu-caret-end {
      left: auto;
      right: 0px;
    }
  }

  &.is-disabled {
    pointer-events: none;
    opacity: 0.45;
  }

  &.is-outline:not(.is-disabled) {
    &:hover,
    &.is-open {
      border-color: rgba(34, 36, 38, 0.35);
    }
    &:focus {
      border-color: #96c8da;
    }
  }

  > .dropdown-caret,
  > .dropdown-clear {
    position: relative;
    width: auto;
    height: auto;
    margin: 0 0 0 0.2em;
    padding: 0;
    opacity: 0.8;
    transition:
      opacity 0.1s ease,
      transform 0.1s ease;
  }

  &.is-outline > .dropdown-caret,
  &.is-outline > .dropdown-clear {
    position: absolute;
    top: 50%;
    right: 0.75em;
    margin: 0;
    transform: translateY(-50%);
    z-index: 3;
  }

  &.is-open.is-outline > .dropdown-caret {
    transform: translateY(-50%) rotate(180deg);
  }

  &.is-clearable {
    &:hover > .dropdown-clear,
    &.is-open > .dropdown-clear {
      opacity: 1;
    }
  }

  > ${StyledDropdownText} {
    font-weight: inherit;
    color: inherit;
  }

  &.is-outline:not(.is-combo) > ${StyledDropdownText} {
    display: block;
    line-height: 1.2em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }

  &.is-combo > ${StyledDropdownText} {
    position: static;
    padding: 0;
    max-width: 100%;
    margin: 0.5em 0 0.5em 0.6em;
    line-height: 1.2em;
  }

  > ${Label} {
    margin: 0.1rem 0.3rem 0.1rem 0;
    max-width: 100%;
    box-shadow: 0 0 0 1px rgba(34, 36, 38, 0.15) inset;
    user-select: none;
  }

  &.is-search {
    > ${StyledDropdownText} {
      position: absolute;
      left: 1em;
      right: 2.1em;
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
      line-height: 1.2em;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &.is-search.is-combo {
    > ${StyledDropdownSearch} {
      position: static;
      width: 2.5em;
      flex: 1 1 2.5em;
      max-width: 100%;
      min-width: 2.5em;
      padding: 0;
      margin: 0.125rem 0;
      height: auto;
      min-height: 1.5em;
    }

    > ${StyledDropdownText} {
      position: absolute;
      top: 50%;
      right: 2.75em;
      left: 1em;
      margin: 0;
      transform: translateY(-50%);
      pointer-events: none;
    }

    > ${Label} ~ ${StyledDropdownText} {
      display: none;
    }
  }

  &.is-combo:not(.is-search) {
    > ${Label} ~ ${StyledDropdownText} {
      display: none;
    }
  }

  &.is-loading {
    &::before {
      content: "";
      position: absolute;
      right: 2.5em;
      top: 50%;
      width: 1em;
      height: 1em;
      margin-top: -0.5em;
      border: 0.2em solid rgba(0, 0, 0, 0.1);
      border-top-color: rgba(0, 0, 0, 0.55);
      border-radius: 50%;
      animation: simplydo-dropdown-spin 0.6s linear infinite;
      z-index: 3;
    }
  }

  &:has(.${buttonBaseClass}) {
    margin-right: 0.5rem;

    &:last-child {
      margin-right: 0rem;
    }
  }

  @keyframes simplydo-dropdown-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const StyledDropdownMenu = styled.div`
  max-width: min(100vw - 16px, 40em);
  margin: 0;
  padding: 0;
  background: #ffffff;
  font-size: 1em;
  text-align: left;
  box-shadow:
    0 2px 4px 0 rgba(34, 36, 38, 0.12),
    0 2px 10px 0 rgba(34, 36, 38, 0.15);
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 0.3rem;
  cursor: auto;
  outline: none;
  max-height: max(16em, 70vh);
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;

  /* Nested Dropdown (item trigger) rows stack like DropdownItem. */
  > ${StyledDropdown}.is-trigger {
    display: block;
    width: 100%;
    max-width: none;
  }
`;

export const StyledDropdownLabelRemove = styled.i`
  cursor: pointer;
  font-size: 1em;
  line-height: 1;
  opacity: 0.5;
  font-style: normal;
  font-weight: bold;
  transition: opacity 0.5s ease;

  &:hover {
    opacity: 1;
  }
`;

export const StyledDropdownItem = styled.div<{
  $highlighted?: boolean;
  $selectable?: boolean;
  $disabled?: boolean;
}>`
  display: flex;
  align-items: center;
  position: relative;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  border: none;
  height: auto;
  text-align: left;
  line-height: 1em;
  color: ${({ $disabled }) =>
    $disabled ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.87)"};
  padding: ${({ $selectable }) =>
    $selectable ? "0.8rem 1.2em 0.8rem 2rem" : "0.8rem 1.2rem"};
  font-size: 1rem;
  font-weight: 400;
  text-transform: none;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: min(500px, 50vw);
  min-width: 100%;
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  background: ${({ $highlighted, $disabled }) => {
    if ($disabled) return "transparent";
    if ($highlighted) return "rgba(0, 0, 0, 0.03)";
    return "transparent";
  }};

  > .dropdown-check {
    position: absolute;
    left: 0.6rem;
    top: 50%;
    transform: translateY(-50%);
    margin-right: 0;
    pointer-events: none;
  }

  &.is-highlighted {
    background: ${({ $disabled }) =>
      $disabled ? "transparent" : "rgba(0, 0, 0, 0.03)"};
  }

  &:hover {
    background: ${({ $disabled }) =>
      $disabled ? "transparent" : "rgba(0, 0, 0, 0.05)"};
    color: ${({ $disabled }) =>
      $disabled ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.95)"};
  }

  > ${Icon},
  > ${Image},
  > img,
  > ${Label} {
    margin-right: 0.6rem;
    flex-shrink: 0;
  }

  > img,
  > ${Image} img {
    width: auto;
    max-height: 2em;
  }
`;

export const StyledDropdownHeader = styled.div`
  border: none;
  margin: 0.8em 1em;
  background: none;
  color: rgba(0, 0, 0, 0.7);
  text-transform: none;
  font-weight: bold;
  font-size: 0.8em;
  pointer-events: none !important;
`;

export const StyledDropdownDivider = styled.div`
  border-top: 1px solid rgba(34, 36, 38, 0.1);
  height: 0;
  margin: 0.5em 0;
`;
